import { useEffect, useCallback } from 'react';

/**
 * External keyboard shortcuts for the fortune-sheet spreadsheet.
 *
 * Cmd/Ctrl+Shift+1  — Number format   (#,##0.00)
 * Cmd/Ctrl+Shift+2  — Time format     (h:mm AM/PM)
 * Cmd/Ctrl+Shift+3  — Date format     (yyyy-mm-dd)
 * Cmd/Ctrl+Shift+4  — Currency format ($#,##0.00)
 * Alt/Opt + =       — AutoSum (inserts =SUM(range) above the active cell)
 *
 * These run in the capture phase so they fire before fortune-sheet's own
 * keydown handlers (which use bubble phase).
 */
export function useSpreadsheetKeyboard(containerRef, workbookRef) {
  const handleKeyDown = useCallback((e) => {
    const isMeta = e.metaKey || e.ctrlKey;

    if (isMeta && e.shiftKey && e.code === 'Digit1') {
      applyFormatToSelection(workbookRef, '#,##0.00', 'n');
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (isMeta && e.shiftKey && e.code === 'Digit2') {
      applyFormatToSelection(workbookRef, 'h:mm AM/PM', 'd');
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (isMeta && e.shiftKey && e.code === 'Digit3') {
      applyFormatToSelection(workbookRef, 'yyyy-mm-dd', 'd');
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (isMeta && e.shiftKey && e.code === 'Digit4') {
      applyFormatToSelection(workbookRef, '$#,##0.00', 'n');
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // Alt/Opt + = : AutoSum
    if (e.altKey && (e.key === '=' || e.key === '+' || e.code === 'Equal')) {
      handleAutoSum(workbookRef);
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  }, [workbookRef]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('keydown', handleKeyDown, true);
    return () => el.removeEventListener('keydown', handleKeyDown, true);
  }, [containerRef, handleKeyDown]);
}

function applyFormatToSelection(workbookRef, formatStr, typeCode) {
  const wb = workbookRef.current;
  if (!wb) return;
  const sel = wb.getSelection();
  if (!sel?.length) return;
  sel.forEach((s) => {
    const r0 = s.row[0], r1 = s.row[1];
    const c0 = s.column[0], c1 = s.column[1];
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        wb.setCellFormat(r, c, 'ct', { fa: formatStr, t: typeCode });
      }
    }
  });
}

function handleAutoSum(workbookRef) {
  const wb = workbookRef.current;
  if (!wb) return;
  const sel = wb.getSelection();
  if (!sel?.length) return;
  const s = sel[0];
  const row = s.row[0];
  const col = s.column[0];
  // Find contiguous non-empty cells going upward
  let startRow = row - 1;
  while (startRow >= 0) {
    const val = wb.getCellValue(startRow, col);
    if (val == null || val === '') break;
    startRow--;
  }
  startRow++; // first non-empty row above
  if (startRow >= row) return; // nothing to sum
  const colLetter = idxToColLetter(col);
  const range = `${colLetter}${startRow + 1}:${colLetter}${row}`;
  wb.setCellValue(row, col, `=SUM(${range})`);
}

function idxToColLetter(idx) {
  let col = '';
  let n = idx + 1;
  while (n > 0) {
    n--;
    col = String.fromCharCode(65 + (n % 26)) + col;
    n = Math.floor(n / 26);
  }
  return col;
}
